import React, {useEffect, useState} from 'react';
import {type Course, getFilteredCourse} from '../../api/courcesService';
import './CourseDetail.css';
import CertificateProgress from "./components/CertificatProgress/CertificateProgress";
import CourseDetailBanner from "../../components/CourseDetailBanner";
import {useAppState} from "../../store/useAppState";
import CourseChapter from './components/CourseChapter';
import {smartNavigate} from '../../utils/smartNavigate';
import {ChapterProgressInfo, getProgressInfo} from "../../utils/getProgressInfo";

interface CourseDetailProps {
  course: Course;
  courseWithProgress: Course;
}

const CourseDetail: React.FC<CourseDetailProps> = ({course, courseWithProgress}) => {
  const {loading} = useAppState((state) => state);
  const [expandedChapters, setExpandedChapters] = useState<Set<number>>(new Set());

  // Detect which chapter should be expanded on mount/update based on progress
  useEffect(() => {
    if (!course || !course.chapters || course.chapters.length === 0) {
      setExpandedChapters(new Set());
      return;
    }
    let chapterToExpand: ChapterProgressInfo | null;

    const courseProgressInfo = getProgressInfo(course, courseWithProgress);

    const finishedChapters = courseProgressInfo.filter(courseProgress => courseProgress.isChapterFinished);
    chapterToExpand = courseProgressInfo.length === finishedChapters.length ? finishedChapters[finishedChapters.length - 1] : courseProgressInfo[finishedChapters.length];
    console.log('chapters info', courseProgressInfo, 'to expand', chapterToExpand);
    if (chapterToExpand) {
      setExpandedChapters(new Set([chapterToExpand.chapterIdx]))
    }

  }, [course, courseWithProgress]);

  // Function to get the appropriate progress message based on current progress
  const getProgressMessage = (currentProgress: number) => {
    // Use progressMessages from course data, fallback to default if not available
    const progressMessages = course.progressMessages || [
      {
        id: 1,
        title: "Welcome to the journey!",
        description: "Let's begin!",
        percentProgress: 0
      }
    ];

    // Sort messages by percentProgress in descending order to find the highest applicable message
    const sortedMessages = [...progressMessages].sort((a, b) => b.percentProgress - a.percentProgress);

    // Find the first message where current progress is >= the message's percentProgress
    const applicableMessage = sortedMessages.find(message => currentProgress >= message.percentProgress);

    // Return the applicable message or the first message as fallback
    return applicableMessage || progressMessages[0];
  };

  const handleMoveNext = async () => {
    try {
      const coursesData = await getFilteredCourse('recommended'); // recommended | trending | promoted

      if (!coursesData || coursesData.length === 0) {
        console.log('No recommended courses available');
        return;
      }

      // Find current course index in the recommended courses list
      const currentCourseIndex = coursesData.findIndex(c => c.id === course.id);

      let nextCourse: Course;

      if (currentCourseIndex === -1) {
        // Current course not found in recommended list, navigate to first course
        nextCourse = coursesData[0];
        console.log('Current course not found in recommended list, navigating to first course:', nextCourse.title);
      } else if (currentCourseIndex === coursesData.length - 1) {
        // Current course is the last in the list, navigate to first course
        nextCourse = coursesData[0];
        console.log('Current course is last in recommended list, navigating to first course:', nextCourse.title);
      } else {
        // Navigate to next course in the list
        nextCourse = coursesData[currentCourseIndex + 1];
        console.log('Navigating to next course:', nextCourse.title);
      }

      // Navigate to the next course
      smartNavigate(`/course/${nextCourse.id}`);

    } catch (error) {
      console.error('Error navigating to next course:', error);
    }
  }

  const toggleChapter = (chapterIndex: number) => {
    setExpandedChapters((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (newSet.has(chapterIndex)) {
        newSet.delete(chapterIndex);
      } else {
        newSet.add(chapterIndex);
      }
      return newSet;
    });
  };

  // Calculate progress percentage based on started and finished lessons
  const calculateOverallProgress = (): number => {
    if (!course || !course.chapters || !courseWithProgress || !courseWithProgress.progressInfo) return 0;

    let totalLessons = 0;
    let finishedLessons = courseWithProgress.progressInfo.finishedLessons.length;

    // Calculate total lessons
    course.chapters?.forEach(chapter => {
      if (chapter.lessons) {
        totalLessons += chapter.lessons.length;
      }
    });

    // Calculate progress based on finished lessons
    return totalLessons > 0 ? Math.round((finishedLessons / totalLessons) * 100) : 0;
  };

  const progressPercent = calculateOverallProgress();
  const currentProgressMessage = getProgressMessage(progressPercent);

  return (
    <div className="course-detail-container">
      <CourseDetailBanner
        courseTitle={course?.title || ''}
        loading={loading}
        progressPercent={progressPercent}
        messageTitle={currentProgressMessage?.title}
        messageDescription={currentProgressMessage?.description}
      />
      <div className="course-detail-levels">
        {course.chapters && course.chapters.length > 0 ? (
          course.chapters.map((chapter, chapterIdx) => {

            return (
              <CourseChapter
                key={chapter.id}
                chapter={chapter}
                chapterIdx={chapterIdx}
                course={course}
                courseWithProgress={courseWithProgress}
                expanded={expandedChapters.has(chapterIdx)}
                onToggle={() => toggleChapter(chapterIdx)}
              />
            )
          })
        ) : (
          <div className="course-detail-level-item locked">No levels available.</div>
        )}
      </div>

      <CertificateProgress
        courseId={course.id}
        progressPercent={progressPercent}
        progressMessages={course.progressMessages}
        courseTitle={course.title}
        onJumpAhead={handleMoveNext}
      />
    </div>
  );
};

export default CourseDetail;
